import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { DEFAULT_PAGINATION_PAGE_NUMBER, DEFAULT_PAGINATION_PAGE_SIZE } from '@app/config/defaults.config';

// Models
import { PagedList } from '@app/types/paged-list';
import { HttpErrorResponse, HttpParams, HttpStatusCode } from '@angular/common/http';
import {
  fileToMediaFileModelAsync,
  mapAnalyticsFiltersToModel,
  mapIncidentsFiltersToModel,
} from '@app/utils/incidents/incidents.utils';

// Services
import { ApiService } from '../api/api.service';

// Types
import { AnalyticsFilters } from '@app/types/analytics-filters';
import { IncidentsFilters } from '@app/types/incidents-filters';

// Utils
import { SortDirection } from '@angular/material/sort';
import { IncidentBaseModel, IncidentModel } from '@app/models/incident.model';
import { IncidentsBatchResultModel } from '@app/models/incidents-batch-result.model';
import { AnalyticsModel } from '@app/models/analytics.model';
import { removeEmptyValuesFromObject } from '@app/utils/object/object.utils';
import { IncidentsJsonPatch } from './incidents-json-patch';
import { IncidentReferencesEditModel } from '@app/models/incident-references-edit.model';
import { IncidentPatchModel } from '@app/pages/incidents/details-page/models/incident-patch.model';
import { IncidentCreateModel } from '@app/pages/incidents/details-page/models/incident-create.model';
import { PendingIncidentCreateModel } from '@app/pages/incidents/details-page/models/pending-incident-create.model';
import { IncidentEditModel } from '@app/pages/incidents/details-page/models/incident-edit-model';

@Injectable({
  providedIn: 'root',
})
export class IncidentsService {
  private readonly apiService = inject(ApiService);

  public getIncidents(
    filters: IncidentsFilters,
    pageNumber: number = DEFAULT_PAGINATION_PAGE_NUMBER,
    pageSize: number = DEFAULT_PAGINATION_PAGE_SIZE,
    sortBy?: string,
    sortDirection: SortDirection = '',
  ): Observable<PagedList<IncidentBaseModel>> {
    const resourceUrl = 'incidents';
    let httpParams = new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize);

    if (sortBy) {
      httpParams = httpParams.set('sortBy', sortBy).set('sortDirection', sortDirection);
    }

    return this.apiService.post<PagedList<IncidentBaseModel>>(
      resourceUrl,
      JSON.stringify(mapIncidentsFiltersToModel(filters)),
      httpParams,
      'getIncidents',
    );
  }

  public getBatchIncidentsByIds(incidentIds: number[]): Observable<IncidentsBatchResultModel> {
    const resourceUrl = 'incidents/batch';

    if (incidentIds.length === 0) {
      return of({
        incidents: [],
        startIndex: 0,
        size: 0,
      });
    }

    const bodyParams = {
      incidentIds,
      startIndex: 0,
      size: incidentIds.length,
    };

    return this.apiService.post<IncidentsBatchResultModel>(resourceUrl, bodyParams, void 0, 'getBatchIncidentsByIds');
  }

  public getOriginalIncidentDetails(incidentId: number): Observable<IncidentModel> {
    const resourceUrl = `incidents/${incidentId}/original`;
    return this.apiService.getJson<IncidentModel>(resourceUrl, void 0, 'getOriginalIncidentDetails');
  }

  public getIncidentDetails(incidentId: string): Observable<IncidentModel> {
    const resourceUrl = `incidents/${incidentId}`;
    return this.apiService.getJson<IncidentModel>(resourceUrl, void 0, 'getIncidentDetails');
  }

  public createIncident(incidentCreateModel: IncidentCreateModel): Observable<number> {
    const resourceUrl = `incidents/create`;
    return this.apiService.post<number>(resourceUrl, incidentCreateModel, undefined, 'createIncident');
  }

  public createPendingIncident(pendingIncidentCreateModel: PendingIncidentCreateModel): Observable<number> {
    const resourceUrl = 'incidents/createPending';
    return this.apiService.post<number>(resourceUrl, pendingIncidentCreateModel, undefined, 'createPendingIncident');
  }

  public patchIncidentDetails(
    incidentId: number,
    incidentVersion: string,
    incidentPatchModel: IncidentPatchModel,
  ): Observable<IncidentModel> {
    const resourceUrl = `incidents/${incidentId}`;

    const incidentsJsonPatch = new IncidentsJsonPatch();
    // Apply database concurrency check
    incidentsJsonPatch.addTestOperation('version', incidentVersion);
    // Add replace operations for every field where data changed
    (
      (Object.keys(incidentPatchModel) as (keyof IncidentPatchModel)[]).filter(
        (key) =>
          key !== 'technicalSteps' &&
          key !== 'duplicateIncidentIdsToDetach' &&
          key !== 'duplicateIncidentIdsToAttach' &&
          incidentPatchModel[key] !== undefined,
      ) as Exclude<
        keyof IncidentPatchModel,
        'technicalSteps' | 'duplicateIncidentIdsToDetach' | 'duplicateIncidentIdsToAttach'
      >[]
    ).forEach((key) => {
      incidentsJsonPatch.addReplaceOperation(key, incidentPatchModel[key]);
    });
    const incidentEditModel: IncidentEditModel = {
      jsonPatch: incidentsJsonPatch.getOperations(),
      technicalSteps: incidentPatchModel.technicalSteps == undefined ? null : incidentPatchModel.technicalSteps,
      duplicateIncidentIdsToDetach: incidentPatchModel.duplicateIncidentIdsToDetach ?? [],
      duplicateIncidentIdsToAttach: incidentPatchModel.duplicateIncidentIdsToAttach ?? [],
    };

    return this.apiService.patch<IncidentModel>(resourceUrl, incidentEditModel, undefined, 'patchIncidentDetails');
  }

  public async editIncidentReferences(
    incidentId: number,
    editModel: IncidentReferencesEditModel,
  ): Promise<Observable<IncidentModel>> {
    const resourceUrl = `incidents/${incidentId}/references`;

    const mediaReferencesToAdd = await fileToMediaFileModelAsync(editModel.mediaReferencesToAdd);
    const bodyParams = removeEmptyValuesFromObject({
      ...editModel,
      mediaReferencesToAdd,
    });

    return this.apiService.post<IncidentModel>(resourceUrl, bodyParams, void 0, 'editIncidentReferences');
  }

  public checkIncidentVersion(incidentId: number, incidentVersion: string): Observable<boolean> {
    const resourceUrl = `incidents/${incidentId}`;

    const incidentsJsonPatch = new IncidentsJsonPatch();
    // Apply database concurrency check
    incidentsJsonPatch.addTestOperation('version', incidentVersion);

    const incidentEditModel: IncidentEditModel = {
      jsonPatch: incidentsJsonPatch.getOperations(),
      technicalSteps: null,
      duplicateIncidentIdsToDetach: [],
      duplicateIncidentIdsToAttach: [],
    };

    return this.apiService
      .patch<IncidentModel>(resourceUrl, incidentEditModel, undefined, 'patchIncidentDetails', true)
      .pipe(
        switchMap(() => of(true)),
        catchError((error) => {
          if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.Conflict) {
            return of(false);
          }

          return of(true);
        }),
      );
  }

  public detachIncident(incidentId: number): Observable<IncidentModel> {
    const resourceUrl = `incidents/${incidentId}/detachIncident`;

    return this.apiService.post<IncidentModel>(resourceUrl, {}, undefined, 'detachIncident');
  }

  public addDuplicateIncident(
    masterIncidentId: number,
    duplicateCandidateIncidentId: number,
  ): Observable<IncidentModel> {
    const resourceUrl = `incidents/${masterIncidentId}/${duplicateCandidateIncidentId}/addDuplicateIncident`;

    return this.apiService.post<IncidentModel>(resourceUrl, {}, undefined, 'addDuplicateIncident');
  }

  // Analytics
  public getIncidentAnalytics(filters: AnalyticsFilters): Observable<AnalyticsModel> {
    const resourceUrl = 'incidents/getIncidentAnalytics';
    const bodyParams = JSON.stringify(removeEmptyValuesFromObject(mapAnalyticsFiltersToModel(filters)));

    if (bodyParams === '{}') {
      return of({
        analyticsPeriodA: void 0,
        analyticsPeriodB: void 0,
      });
    }

    return this.apiService.post<AnalyticsModel>(resourceUrl, bodyParams, void 0, 'getIncidentAnalytics');
  }
}
